import { useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { auth } from "@/lib/auth"
import { Button, Input, Spinner, Toast } from "@/components/ui"

interface ResetSearch {
  token?: string
}

export const Route = createFileRoute("/reset")({
  validateSearch: (s: Record<string, unknown>): ResetSearch => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: ResetPage,
})

function ResetPage() {
  const { token } = Route.useSearch()
  const nav = useNavigate()
  const [pw, setPw] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token) { setError("Missing reset token. Open the link from your email."); return }
    if (pw.length < 12) { setError("Password must be at least 12 characters."); return }
    setBusy(true)
    setError(null)
    try {
      await auth.reset(token, pw)
      await nav({ to: "/login" })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-background">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-border bg-card/90 p-7 shadow-glass backdrop-blur space-y-4"
      >
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Choose a new password</h1>
        </div>
        <Input
          type="password"
          autoComplete="new-password"
          minLength={12}
          required
          value={pw}
          onChange={(e) => setPw(e.target.value)}
        />
        {error && <Toast variant="destructive">{error}</Toast>}
        <Button type="submit" disabled={busy || !token} className="w-full">
          {busy ? <Spinner /> : null}
          Save password
        </Button>
      </form>
    </div>
  )
}
